import { ArrowRightOutlined, CommentOutlined, FlagFilled, LinkOutlined } from '@ant-design/icons'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { OriginalRequest } from '@components/OrderWizard/Panes/ControlPane/PaneComponents/OriginalRequest'
import { BBDTag, GraviButton, Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useLockedOrders } from '@hooks/useLockedOrders'
import { Col, Row } from 'antd'
import React, { useMemo } from 'react'

import { useWizardMaster } from '../../Contexts/WizardMasterContext'
import { BackhaulEditButtons } from './PaneComponents/EditOrderControls/BackhaulEditButtons'
import { BackhaulProducts } from './PaneComponents/EditOrderControls/BackhaulProducts'
import { EditOrderButtons } from './PaneComponents/EditOrderControls/EditOrderButtons'
import { OrderNoteForm } from './PaneComponents/EditOrderControls/OrderNoteForm'
import { PaneHeader } from './PaneComponents/Layout/PaneHeader'
import { TankImpactRows } from './PaneComponents/TankImpactRows'
import { ViewAccessorialsModal } from './PaneComponents/ViewAccessorialsModal'

export function TankImpact({ cancelOrder, resetOrder }) {
  const { wizardOrder, isBackhaul, viewingNoteForm, setViewingNoteForm, driverNote, rundownToggled } = useWizardMaster()
  const { scopes } = useSecurityContext()
  const { lockedOrders } = useLockedOrders()
  const optimizationState = useMemo(
    () => lockedOrders?.find((order) => order.order_id === wizardOrder.id)?.state,
    [lockedOrders, wizardOrder.id]
  )
  if (!wizardOrder) return null
  const showEditControls =
    !optimizationState &&
    scopes?.create_order &&
    !wizardOrder.is_linked &&
    !viewingNoteForm &&
    wizardOrder.carrier_info?.crossroads_status !== 'assigned'

  const showDriverNoteButton =
    !optimizationState && scopes.config.notes_enabled && !scopes.is_carrier && wizardOrder.state !== 'complete'

  const showAccessorialButton = wizardOrder.state !== 'accepted' && wizardOrder.id

  const showOrderResetBtn = wizardOrder.can_reset

  return (
    <>
      <PaneHeader title='Order Destination(s)' showStatus>
        <Texto category='h4' appearance='secondary' weight='bold' level={3}>
          {isBackhaul && wizardOrder.destination_name}
          {wizardOrder &&
            wizardOrder.drops?.length > 0 &&
            wizardOrder.drops?.map((o, i) => {
              if (i === 0) {
                return <span key={i}>{o.location_name}</span>
              }
              return (
                <React.Fragment key={i}>
                  <ArrowRightOutlined style={{ color: 'var(--theme-color-2)' }} className='pl-1 pr-2' />
                  {o.location_name}
                </React.Fragment>
              )
            })}
        </Texto>
      </PaneHeader>
      <Vertical flex={1} className='pane-content'>
        {isBackhaul ? (
          <BackhaulProducts order={wizardOrder} />
        ) : (
          <TankImpactRows affectedTanks={wizardOrder.affected_tanks} rundownToggled={rundownToggled} />
        )}

        <Vertical
          gap={8}
          flex='0'
          style={{ overflow: 'visible' }}
          justifyContent='flex-end'
          className='order-edit-actions'
        >
          {wizardOrder.is_linked && <LinkedMessage />}
          {wizardOrder.carrier_info?.crossroads_status === 'assigned' && <CrossroadsMessage order={wizardOrder} />}
          {viewingNoteForm && (
            <>
              <Horizontal bg='elevated' border='all' className='p-3'>
                <Texto category='heading-small'>{driverNote ? 'Edit Driver Note' : 'Add Driver Note'}</Texto>
              </Horizontal>

              <OrderNoteForm />
            </>
          )}
          <OriginalRequest />
          {showEditControls && (
            <Vertical border='all' className='px-3 pb-3' bg='elevated' gap={8}>
              <Horizontal style={{ margin: '0 -16px' }} bg='elevated' border='all' className='p-3'>
                <Texto category='heading-small'>Edit Order</Texto>
              </Horizontal>
              {showAccessorialButton && (
                <ViewAccessorialsModal
                  orderId={wizardOrder.id}
                  freightCustomers={wizardOrder.freight_customers}
                  isComplete={wizardOrder.state === 'complete'}
                />
              )}
              {showDriverNoteButton && (
                <GraviButton
                  size='large'
                  appearance='outlined'
                  buttonText={driverNote ? 'Edit Driver Note' : 'Add Driver Note'}
                  onClick={() => {
                    setViewingNoteForm(true)
                  }}
                  icon={<CommentOutlined />}
                />
              )}
              {isBackhaul ? (
                <BackhaulEditButtons
                  cancelOrder={cancelOrder}
                  resetOrder={resetOrder}
                  showOrderResetBtn={showOrderResetBtn}
                  optimizing={optimizationState}
                />
              ) : (
                <EditOrderButtons
                  cancelOrder={cancelOrder}
                  resetOrder={resetOrder}
                  showOrderResetBtn={showOrderResetBtn}
                  isCarrier={!!wizardOrder.carrier}
                  optimizing={optimizationState}
                />
              )}
            </Vertical>
          )}
          {optimizationState === 'pending' ? (
            <Vertical horizontalCenter verticalCenter className='p-4'>
              <BBDTag theme1 style={{ border: '1px solid var(--theme-color-1)' }} className='p-2'>
                <Texto appearance='secondary' category='h5'>
                  Pending
                </Texto>
              </BBDTag>
            </Vertical>
          ) : (
            optimizationState && (
              <Vertical horizontalCenter verticalCenter className='p-4'>
                <BBDTag theme2 style={{ border: '1px solid var(--theme-color-2)' }} className='p-2'>
                  <Texto appearance='secondary' category='h5'>
                    Optimizing
                  </Texto>
                </BBDTag>
              </Vertical>
            )
          )}
        </Vertical>
      </Vertical>
    </>
  )
}

function LinkedMessage() {
  return (
    <div className='linked-message'>
      <Row>
        <Col className='vertical-flex-center pr-4'>
          <LinkOutlined />
        </Col>
        <Col>
          <div className='linked-heading'>Linked Order</div>
          <div className='linked-text'>You will need to unlink this order to edit it. </div>
        </Col>
      </Row>
    </div>
  )
}
function CrossroadsMessage({ order }) {
  return (
    <div className='crossroads-message p-3 px-4'>
      <Horizontal>
        <Col className='vertical-flex-center pr-4'>
          <FlagFilled />
        </Col>
        <Col>
          <div className='linked-heading'>Assigned To Carrier</div>
          <Texto appearance='white' category='p2'>
            This order is being ran by <b>{order.loads[0]?.driver}</b> with <b>{order.carrier}.</b> To edit the order,
            the driver will have to cancel the order from the in-cab app.{' '}
          </Texto>
        </Col>
      </Horizontal>
    </div>
  )
}
